import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/* ─── Security Hero Background ─── */
const SECURITY_HERO_BG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20shield%20and%20protective%20light%20aura%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20particles%20luxurious%20atmosphere%20no%20text&width=1920&height=800&seq=securityhero1&orientation=landscape';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

export default function SecurityPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  useSEO({
    title: t('seo.securityTitle'),
    description: t('seo.securityDescription'),
    keywords: t('seo.securityKeywords'),
    path: '/security',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.securityTitle'), t('seo.securityDescription'), '/security', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.security'), path: '/security' },
        ]),
      ],
    },
  });

  const sections = [
    {
      title: '1. Information Security Management',
      body: 'TradFit.co.,Ltd recognizes the importance of information security and has established comprehensive policies and procedures to protect all information assets, including client data, business information, and personal data.',
    },
    {
      title: '2. Security Measures',
      body: 'We implement multiple layers of security controls including physical security with restricted access to facilities, technical security with encryption and firewalls, administrative security with access controls and training, and network security with secure architecture.',
    },
    {
      title: '3. Data Protection',
      body: 'We protect data through encryption of data in transit and at rest, regular backups and disaster recovery procedures, secure data disposal methods, access controls based on least privilege, and regular security audits.',
    },
    {
      title: '4. Employee Training',
      body: 'All employees undergo regular security awareness training and are required to comply with our information security policies. We maintain strict confidentiality agreements and conduct background checks.',
    },
    {
      title: '5. Incident Response',
      body: 'We maintain a comprehensive incident response plan including immediate detection and containment, investigation and root cause analysis, notification to affected parties, corrective measures, and post-incident review.',
    },
    {
      title: '6. Third-Party Management',
      body: 'We carefully evaluate and monitor all third-party service providers. All vendors must meet our security standards and comply with relevant data protection regulations.',
    },
    {
      title: '7. Compliance',
      body: 'We comply with all applicable laws and regulations regarding information security and data protection, including Japan\'s Act on the Protection of Personal Information (APPI) and relevant industry standards.',
    },
    {
      title: '8. Continuous Improvement',
      body: 'We continuously review and update our security measures to address emerging threats. Regular security assessments and audits ensure our controls remain effective.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title="Security Policy"
        subtitle="Our commitment to protecting your data"
        tag="Security"
        backgroundImage={SECURITY_HERO_BG}
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: '情報セキュリティ' },
        ]}
      />

      <main className="py-16 md:py-24 px-6 md:px-10 max-w-[800px] mx-auto">
        <div className="mb-16">
          <ElegantSectionTitle
            tag="Security Policy"
            title="情報セキュリティ方針"
          />
        </div>

        <div className="space-y-12">
          {sections.map((sec, i) => (
            <section key={i}>
              <h2
                className="text-base mb-4 flex items-center gap-3"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontWeight: 500,
                  color: '#1A1A1A',
                }}
              >
                <span
                  className="w-6 h-px inline-block shrink-0"
                  style={{ background: '#C9A227' }}
                />
                {sec.title}
              </h2>
              <p
                className="text-sm leading-relaxed pl-9"
                style={{
                  color: '#7A7569',
                  fontFamily: "'Noto Serif JP', serif",
                }}
              >
                {sec.body}
              </p>
            </section>
          ))}

          <section>
            <h2
              className="text-base mb-4 flex items-center gap-3"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              <span
                className="w-6 h-px inline-block shrink-0"
                style={{ background: '#C9A227' }}
              />
              9. Reporting Security Concerns
            </h2>
            <p
              className="text-sm leading-relaxed pl-9"
              style={{
                color: '#7A7569',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              If you discover a security vulnerability or have concerns, please contact us through our{' '}
              <Link
                to="/contact"
                className="no-underline transition-colors duration-300"
                style={{ color: '#C9A227' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
                }}
              >
                contact page
              </Link>
              . We take all security reports seriously.
            </p>
          </section>
        </div>
      </main>

      <ElegantFooter />
    </div>
  );
}
